// T6 (docs/build-briefs/T6-pay-key-map-signal.md): the per-target pay
// idempotency key map, extracted from main.ts so its cap, eviction, and
// operational signal are unit-testable (the same extraction shape
// approvalFlow.ts established, for the same reason). Behavior of the
// map itself is unchanged from the main.ts original (independent-review
// Finding A, 2026-08-26, and the gate re-review correction that made it
// per-target): mint-or-reuse per target, explicit clear on definitive
// resolution, insertion-order eviction beyond the cap.
//
// What T6 adds is the SIGNAL. The cap is a defensive backstop (the
// realistic population is zero to one entries), but an eviction is not
// free: the evicted entry is a still-unresolved payment intent, and the
// next retry for that target mints a fresh key, which is exactly the
// double-charge window the per-target map exists to close. So the map
// now reports, through an injected callback so this module stays pure:
//   - once when the population crosses the warning threshold, re-armed
//     only after it falls back below, so a hovering population does not
//     spam; and
//   - on EVERY eviction, naming the evicted target (never a key value:
//     the key is the thing a duplicate charge would replay, and it has
//     no business in a log).

export const PAY_IDEMPOTENCY_KEY_MAP_MAX_ENTRIES = 200;
export const PAY_IDEMPOTENCY_KEY_MAP_WARN_THRESHOLD = 150;

export type PayKeySignal =
  | { kind: "approaching_cap"; size: number }
  | { kind: "evicted_unresolved_target"; target: string; size: number };

export class PayIdempotencyKeyMap {
  private readonly keysByTarget = new Map<string, string>();
  private warnedAboveThreshold = false;
  // Plain field assignments rather than constructor parameter
  // properties: node --test runs this file's own unit suite in
  // strip-only TypeScript mode, which cannot represent parameter
  // properties.
  private readonly mintKey: () => string;
  private readonly signal: (event: PayKeySignal) => void;

  constructor(mintKey: () => string, signal: (event: PayKeySignal) => void) {
    this.mintKey = mintKey;
    this.signal = signal;
  }

  get size(): number {
    return this.keysByTarget.size;
  }

  /** True while the population sits at or above the warning threshold;
   * the debug panel renders this as literal text, never color alone. */
  get aboveWarnThreshold(): boolean {
    return this.keysByTarget.size >= PAY_IDEMPOTENCY_KEY_MAP_WARN_THRESHOLD;
  }

  getOrMint(target: string): string {
    const existing = this.keysByTarget.get(target);
    if (existing !== undefined) {
      return existing;
    }
    const key = this.mintKey();
    this.keysByTarget.set(target, key);
    if (this.keysByTarget.size >= PAY_IDEMPOTENCY_KEY_MAP_WARN_THRESHOLD) {
      if (!this.warnedAboveThreshold) {
        this.warnedAboveThreshold = true;
        this.signal({ kind: "approaching_cap", size: this.keysByTarget.size });
      }
    }
    // Defensive backstop only; Map iteration order is insertion order,
    // so this evicts the single oldest still-unresolved entry.
    while (this.keysByTarget.size > PAY_IDEMPOTENCY_KEY_MAP_MAX_ENTRIES) {
      const oldestTarget = this.keysByTarget.keys().next().value;
      if (oldestTarget === undefined) {
        break;
      }
      this.keysByTarget.delete(oldestTarget);
      this.signal({
        kind: "evicted_unresolved_target",
        target: oldestTarget,
        size: this.keysByTarget.size,
      });
    }
    return key;
  }

  clear(target: string): void {
    this.keysByTarget.delete(target);
    if (this.keysByTarget.size < PAY_IDEMPOTENCY_KEY_MAP_WARN_THRESHOLD) {
      this.warnedAboveThreshold = false;
    }
  }

  /** PAY7 (docs/build-briefs/PAY7-pay-retry-after-terminal-change.md
   * Section 2.8): seeds `target` -> `key` directly, as though it had
   * already been minted, with NO signal fired -- this is not a new
   * pending payment coming into existence, it is the SAME one a prior
   * page load already knew about (restored from sessionStorage). Called
   * once per persisted entry, right after construction, before any real
   * Pay attempt can run. Does not participate in the cap/eviction
   * accounting beyond occupying a slot the same way any other entry
   * does. */
  restore(target: string, key: string): void {
    this.keysByTarget.set(target, key);
  }

  /** PAY7: a pure, non-minting read of `target`'s own pending key, for
   * the debug panel (main.ts's renderPayKeyDebug) to display without
   * mutating state the way getOrMint would. Returns undefined when
   * nothing is pending for this target. */
  peek(target: string): string | undefined {
    return this.keysByTarget.get(target);
  }

  /** PAY19 (docs/build-briefs/PAY19-reopen-sweep-race.md): every target
   * ACTUALLY pending for `orderId` right now, read straight from this
   * map's own keys rather than reconstructed from a guest or split-plan
   * list the caller may not have loaded yet. "whole" and "guest" targets
   * both embed the order id in the target string itself (main.ts's
   * computePayIntentTarget: `whole:<orderId>` and
   * `guest:<orderId>:<guestId>`), so both are matched here with no
   * outside state at all. "share" targets are not: they are keyed only
   * by `shareId`, with no order id embedded, so a share-scoped key is
   * not, and never was, reachable through this method; the caller still
   * walks `state.activeSplitPlan` for that one scope separately, which
   * DEC-026 makes a no-op on a reopened ticket in practice (a split plan
   * can never be active on a ticket that just reopened).
   *
   * This is what lets the caller sweep an order's pending pay keys
   * BEFORE any per-order state (guests, split plan) has loaded for it:
   * reading the map's own keys needs nothing but the order id. Before
   * this method existed, the sweep had to wait until state.guests
   * reflected the reopened order, which meant waiting until after the
   * ring screen was already interactive -- the exact window PAY19
   * describes a bartender minting a fresh key inside, only for it to be
   * swept a moment later by a sweep that started before that mint. */
  targetsForOrder(orderId: string): string[] {
    const wholeTarget = `whole:${orderId}`;
    const guestPrefix = `guest:${orderId}:`;
    // SPLIT1: the No guest payment and every By amount payment embed the order id too.
    const unassignedTarget = `unassigned:${orderId}`;
    const amountPrefix = `amount:${orderId}:`;
    // RCPT1-S3: the receipt authorization, and one capture key per tip written on the receipt.
    const receiptTarget = `receipt:${orderId}`;
    const receiptTipPrefix = `receipttip:${orderId}:`;
    const matches: string[] = [];
    for (const target of this.keysByTarget.keys()) {
      if (
        target === wholeTarget ||
        target === unassignedTarget ||
        target.startsWith(guestPrefix) ||
        target.startsWith(amountPrefix) ||
        target === receiptTarget ||
        target.startsWith(receiptTipPrefix)
      ) {
        matches.push(target);
      }
    }
    return matches;
  }
}
